/**
 * English message catalogue — the source of truth for the message schema.
 *
 * `fr.ts` is typed against `typeof` this object, so adding a key here without
 * its French counterpart fails `vue-tsc` rather than silently falling back.
 *
 * vue-i18n message syntax notes:
 *  - `{name}` interpolates; a literal `@` must be written `{'@'}`
 *  - `a | b` separates plural forms (singular | plural), chosen by the count
 *    passed to `t(key, count)`; `{n}` is implicitly bound to that count
 */
export default {
  common: {
    loading: 'Loading…',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    edit: 'Edit',
    delete: 'Delete',
    previous: 'Previous',
    next: 'Next',
    view: 'View',
    noRecords: 'No records',
    records: '{n} record | {n} records',
    rangeOf: '{from}–{to} of {total}',
  },
  a11y: {
    viewMode: 'View mode',
    sortColumn: 'Sort by {column}',
    progress: 'Run progress',
    hoverValues: 'Use the arrow keys to step through values; Escape clears.',
  },
  pager: {
    label: 'Pagination',
    first: 'First page',
    previous: 'Previous page',
    next: 'Next page',
    last: 'Last page',
    page: 'Page {n}',
  },
  tableExport: {
    selected: 'Selected rows ({n})',
    view: 'Current view ({n})',
    all: 'All matching rows ({total})',
  },
  duration: {
    seconds: '{s}s',
    minutesSeconds: '{m}m {s}s',
    hours: '{h}h',
  },
  table: {
    id: 'ID',
    type: 'Type',
    channels: 'Channels',
    electrodes: 'Electrodes',
    rate: 'Rate',
    duration: 'Duration',
    device: 'Device',
    created: 'Created',
    status: 'Status',
    actions: 'Actions',
  },
  status: {
    running: 'Running',
    done: 'Done',
    ok: 'OK',
    failed: 'Failed',
    cancelled: 'Cancelled',
    skipped: 'Skipped',
    pending: 'Pending',
  },
  titles: {
    login: 'Sign in',
    dashboard: 'Overview',
    explorer: 'Data explorer',
    run: 'Run analysis',
    runs: 'Runs',
    results: 'Results',
    shared: 'Shared dataset',
    insights: 'Dataset insights',
    neurocloud: 'NeuroCloud',
    notes: 'Notes',
    admin: 'Data batches',
    fallback: 'Research platform',
  },
  nav: {
    tagline: 'EEG foundation model',
    sections: {
      melomind: 'Melomind',
      neurocloud: 'NeuroCloud',
    },
    items: {
      dashboard: { label: 'Overview', hint: 'Recent activity' },
      explorer: { label: 'Explorer', hint: 'Browse & select EEG' },
      run: { label: 'Run analysis', hint: 'Submit to REVE' },
      runs: { label: 'Runs', hint: 'Past results' },
      shared: { label: 'Shared dataset', hint: 'Collaborative editing' },
      insights: { label: 'Insights', hint: 'Dataset at a glance' },
      neurocloud: { label: 'NeuroCloud', hint: 'Computed markers & series' },
      notes: { label: 'Notes', hint: 'Your notes' },
      admin: { label: 'Data batches', hint: 'Swap the loaded EEG' },
    },
    updates: 'What’s new',
    language: 'Language',
    primaryNav: 'Main navigation',
    skipToContent: 'Skip to content',
    openMenu: 'Open navigation menu',
    closeMenu: 'Close navigation menu',
    collapse: 'Collapse navigation',
    expand: 'Expand navigation',
    signedIn: 'Signed in',
    signOut: 'Sign out',
    idleWarning: 'Session ending soon — move the cursor to stay signed in',
    themeToLight: 'Switch to light theme',
    themeToDark: 'Switch to dark theme',
  },
  login: {
    tagline: 'EEG foundation model research',
    title: 'Sign in',
    restricted: 'Access is restricted to authorised researchers.',
    email: 'Email',
    emailPlaceholder: "you{'@'}institution.org",
    password: 'Password',
    submit: 'Sign in',
    submitting: 'Signing in…',
    timedOut: 'Your session ended after 30 minutes of inactivity.',
    preparing: 'Loading your workspace…',
    complianceLine1: 'This system processes personal health data.',
    complianceLine2: 'Access is logged and subject to your institution’s data agreement.',
  },
  auth: {
    notConfigured:
      'Authentication is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
    invalidCredentials: 'That email and password combination is not recognised.',
    signInFailed: 'Unable to sign in right now.',
  },
  dashboard: {
    eyebrow: 'Overview',
    title: 'Research workspace',
    stats: {
      eegRecords: 'EEG records',
      sessions: 'Sessions',
      participants: 'Participants',
      surveyResults: 'Survey results',
    },
    mlNotReady: 'The REVE inference service is not ready.',
    mlNotReadyDetail: 'Jobs will stay queued until it becomes available.',
    recentRuns: 'Recent runs',
    viewAll: 'View all',
    open: 'open',
    noRunsYet: 'No runs yet.',
    sharedDataset: 'Shared dataset',
    links: {
      explore: { title: 'Explore the data', hint: 'Browse and select EEG records' },
      run: { title: 'Run an analysis', hint: 'Extract REVE embeddings' },
      manage: { title: 'Manage the dataset', hint: 'Edit shared records together' },
      neurocloud: { title: 'NeuroCloud analytics', hint: 'myBrain’s computed markers & series' },
    },
  },
  explorer: {
    eyebrow: 'Browse & select',
    title: 'Data explorer',
    intro: 'Click a record type to expand it, then click records to add them to your selection.',
    views: {
      canvas: 'Canvas',
      table: 'Table',
      timeline: 'Timeline',
    },
    enterFullscreen: 'Expand to fullscreen',
    enterFullscreenHint: 'Expand the explorer to fullscreen',
    exitFullscreen: 'Exit fullscreen',
    exitFullscreenHint: 'Exit fullscreen (Esc)',
    runAnalysis: 'Run analysis ({n})',
    rootNode: 'EEG dataset',
    unknownMontage: 'unknown montage',
    typeFilter: 'Type: {type}',
    allRecords: 'All records',
    clearFilter: 'clear filter ×',
    sourceFilterLabel: 'Filter by data source',
    sources: {
      melomaind: 'Melomaind',
      ilab: 'Ilab',
    },
    selectedCount: '{n} record selected | {n} records selected',
    signalTotal: '· {duration} of signal',
    mixedMontages:
      '⚠ Mixed montages ({n}). REVE encodes electrode position, so embeddings from different montages are not directly comparable.',
    clearSelection: 'Clear selection',
    resetSelection: 'Reset selection ({n})',
    resetView: 'Reset view',
    showIds: 'View IDs ▾',
    hideIds: 'Hide IDs ▴',
    removeId: 'Remove #{id} from the selection',
    export: {
      selected: 'Selected records ({n})',
      view: 'Current view ({n})',
      type: 'All {type} ({total})',
      all: 'All records ({total})',
    },
  },
  recordsTable: {
    selectRecord: 'Select record {id}',
    selectPage: 'Select page',
  },
  updates: {
    eyebrow: 'Product updates',
    title: 'What’s new',
    sections: {
      before: {
        title: 'Where it started',
        when: 'the foundation',
        i1: 'Data explorer with canvas, table and timeline views for browsing and selecting EEG recordings.',
        i2: 'REVE analysis runs: submit a selection, follow progress, inspect embeddings and results.',
        i3: 'Shared dataset editing for the whole team, with conflict detection and a full audit trail.',
        i4: 'Dataset insights charts and personal notes.',
        i5: 'Data-batch rotation: the loaded EEG slice swaps against the private MEGA archive (admin).',
        i6: 'English/French interface with light and dark themes.',
      },
      today: {
        title: 'The NeuroCloud wave',
        when: 'August 12, 2026',
        i1: 'NeuroCloud integrated end to end: its own section with overview, markers, time series, group stats and preprocessed EEG — 3M+ rows of derived analytics.',
        i2: 'Preprocessed recordings are linked to their raw EEG and can be dropped straight into the explorer selection.',
        i3: 'Melomind / Ilab source filter in the explorer, driven by the electrode montage.',
        i4: 'Full three-way code review with every confirmed fix deployed, including hardened exports and swap safety.',
        i5: 'The platform moved to braim.medigma.ai; all source data is archived on MEGA with verified checksums.',
      },
      now: {
        title: 'Latest refinements',
        when: 'just landed',
        i1: 'Enter signs you in from either login field.',
        i2: 'NeuroCloud tables filter live as you type — no Enter needed.',
        i3: 'Row selection and CSV/JSON export on every NeuroCloud table: selected rows, the current view, or everything matching the filter.',
        i4: 'Numbered page navigation (1 2 3 …) on all tables across the app.',
        i5: 'The time-series tab gained a user picker next to the free id input.',
        i6: 'Closing the window now signs you out.',
        i7: 'This “What’s new” panel.',
      },
    },
  },
  nc: {
    eyebrow: 'Derived analytics',
    title: 'NeuroCloud',
    intro:
      'myBrain’s own analysis of the Melomind recordings: computed markers, windowed curves, group statistics and preprocessed signal — imported read-only alongside the raw data it was derived from.',
    tabsAria: 'NeuroCloud sections',
    tabs: {
      overview: 'Overview',
      markers: 'Markers',
      series: 'Time series',
      groups: 'Group stats',
      preprocessed: 'Preprocessed EEG',
    },
    overview: {
      computedValues: 'Computed values',
      windowed: 'Windowed series',
      preprocessed: 'Preprocessed EEG',
      groupStats: 'Group statistics',
      users: 'Users covered',
      coverage:
        'NeuroCloud preprocessed {n} distinct recordings — {batch} of them are in the currently loaded batch.',
      sessionNote:
        'Session numbers in this dataset are per-user ordinals (#0, #1, #2…). They do not reference the sessions table.',
      versions: 'Pipeline versions',
    },
    markers: {
      filterName: 'Filter by marker name…',
      filterUser: 'User id',
      user: 'User',
      session: 'Session',
      name: 'Marker',
      value: 'Value',
    },
    series: {
      pickUser: 'User id',
      userSelect: 'Pick a user…',
      sessionsCount: '{n} session | {n} sessions',
      load: 'Load sessions',
      session: 'Session',
      empty: 'No windowed series for this user.',
      hint: 'Pick a user from the list (or type an id) to browse their windowed marker curves.',
      pick: 'Pick one or more series on the left.',
    },
    export: {
      selectRow: 'Select row {id}',
      clearSelection: 'Clear selection ({n})',
    },
    groups: {
      search: 'Search tests…',
      group: 'Group',
      name: 'Test',
      data: 'Result',
    },
    pre: {
      onlyLoaded: 'Only the loaded batch',
      ingest: 'Raw record',
      element: 'Part',
      batch: 'Batch',
      inBatch: 'in batch',
      otherBatch: 'other batch',
      header: 'Header',
      outOfBatch:
        'This recording lives in another batch — swap batches to work with its raw signal.',
      addToSelection: 'Add raw record to selection',
      inSelection: 'In selection ✓',
    },
  },
  timeline: {
    loading: 'Loading collection timeline…',
    facetAria: 'Monthly record counts for {label}',
    otherTypes: 'Other ({n} type) | Other ({n} types)',
    peak: 'peak {count} · {month}',
    browse: 'browse →',
    empty: 'No timeline data.',
  },
  run: {
    eyebrow: 'Analysis',
    title: 'Run REVE',
    intro:
      'Extract embeddings from the selected recordings. The run continues on the server — you can close this tab and come back to it later.',
    emptyTitle: 'No records selected.',
    goToExplorer: 'Go to the explorer',
    selectedRecordings: 'Selected recordings',
    clearAll: 'Clear all',
    clearAllConfirm: 'Clear all {n}?',
    clearAllHint: 'Remove every selected recording',
    clearAllConfirmHint: 'Click again to remove every selected recording',
    remove: 'remove',
    preprocessing: 'Preprocessing',
    windowSeconds: 'Window (seconds)',
    hopSeconds: 'Hop (seconds)',
    bandLow: 'Band low (Hz)',
    bandHigh: 'Band high (Hz)',
    notch: 'Notch (Hz)',
    normalisation: 'Normalisation',
    normRobust: 'Robust z-score (median/IQR)',
    normZscore: 'Z-score',
    normNone: 'None',
    maxWindows: 'Max windows per record',
    keepWindowEmbeddings: 'Keep per-window embeddings',
    tooShort:
      '{count} of {total} selected records is likely too short for a {window}s window and will be reported as skipped. | {count} of {total} selected records are likely too short for a {window}s window and will be reported as skipped.',
    tooShortDetail:
      'A {window}s window at 200 Hz needs about {needed}s of 250 Hz source signal. Lower the window length to include them.',
    mixedMontages:
      'Selection mixes {n} electrode montages. REVE encodes electrode position, so these embeddings will not be directly comparable to each other.',
    usableCount:
      '{n} record expected to produce embeddings | {n} records expected to produce embeddings',
    submit: 'Start run',
    submitting: 'Submitting…',
  },
  runs: {
    eyebrow: 'History',
    title: 'Runs',
    intro: 'Your analysis runs. These are private to your account.',
    submitted: 'Submitted',
    records: 'Records',
    batch: 'Batch',
    progress: 'Progress',
    failedCount: '({n} failed)',
    deleteConfirm: 'Really delete?',
    deleteHint: 'Delete this run and its results',
    deleteConfirmHint: 'Click again to permanently delete this run and its results',
    emptyLead: 'No runs yet.',
    emptyLink: 'Select some records',
    emptyTail: 'to begin.',
  },
  results: {
    eyebrow: 'Run results',
    title: 'Analysis output',
    reportPdf: 'Report (PDF)',
    reportBuilding: 'Building report…',
    reportHint:
      'A PDF report with every visualisation: projection, heatmap strips and dimension profiles',
    allRuns: 'All runs',
    processed: '{processed} of {total} processed',
    failedInline: '· {n} failed',
    liveHint: 'Running on the server — you can close this tab',
    tiles: {
      records: 'Records',
      embedded: 'Embedded',
      skipped: 'Skipped',
      failed: 'Failed',
      windows: 'Windows',
    },
    embeddingDim: 'Embedding dimension:',
    embeddingDimTail: '— REVE encoder output, mean-pooled across windows.',
    ingest: 'Ingest',
    windows: 'Windows',
    sourceHz: 'Source Hz',
    dim: 'Dim',
    detail: 'Detail',
    embedding: 'Embedding',
    show: 'Show',
    hide: 'Hide',
    embeddingFor: 'Embedding — record #{id}',
    viz: {
      heatmap: 'Heatmap',
      bars: 'Bars',
      raw: 'Raw',
    },
    tabs: {
      list: 'List of records ({n})',
      projections: 'Projections',
    },
    report: {
      all: 'All',
      allHint: 'every visualisation',
      heatmap: 'Heatmap',
      heatmapHint: 'colour strips',
      bars: 'Bars',
      barsHint: 'dimension profiles',
      raw: 'Raw',
      rawHint: 'numeric vectors',
    },
    projection: {
      other: 'Other',
      unknownType: 'untyped',
      windows: '{n} window | {n} windows',
      clickHint: 'Click a point to open that record in the list.',
    },
    searchPlaceholder: 'Filter records…',
    loadingEmbeddings: 'Loading all embeddings of this run…',
    needTwo: 'The projection needs at least two embedded records.',
    noResults: 'No results produced.',
    waitingWorker: 'Waiting for the worker…',
  },
  insights: {
    eyebrow: 'Shared data',
    title: 'Dataset insights',
    intro:
      'The shared dataset, visualised as it is in the database. Embedding views (heatmaps, projections) need REVE vectors and live on each run’s results page; these are the shapes the raw data itself carries.',
    eegDuration: 'EEG recording durations',
    sessionDuration: 'Session durations',
    sessionsMonthly: 'Sessions per month',
    performanceByType: 'Mean performance by exercise type',
    surveyScores: 'Mean survey score by mode',
    scatterTitle: 'Session duration × performance',
    scatterHint: 'A random sample of 1,500 sessions, coloured by exercise type.',
    scatterX: 'duration (s)',
    scatterY: 'performance',
    embeddingNote:
      'Looking for heatmaps, dimension profiles or projections of this data? Those read REVE embeddings, which only exist once records have been through an analysis — select records in the explorer, start a run, and its results page carries all of them.',
  },
  shared: {
    eyebrow: 'Collaborative',
    title: 'Shared dataset',
    intro:
      'The imported working copy. Everyone on the team sees and edits the same records — this view refreshes every 15 seconds.',
    searchPlaceholder: 'Search…',
    facetAll: 'All',
    notSearchable: 'This table has no searchable columns',
    syncedAt: '· synced {time}',
    saved: 'Change saved.',
    alreadyDeleted: 'This record has already been deleted — nothing to do here.',
    keptTheirs: 'Kept the other user’s version.',
    editCell: 'Edit {col} of record {id}',
    invalidJson: 'That value is not valid JSON — fix it or cancel the edit.',
    deleteConfirm: 'Delete {table} #{id}? It can be restored by an operator.',
    deleted: 'Record #{id} deleted.',
    export: {
      selected: 'Selected rows ({n})',
      view: 'Current view ({n})',
      table: 'Whole table ({total})',
      all: 'All tables',
      busy: 'Exporting… {mb} MB',
      done: '{n} rows exported',
      selectPage: 'Select every row on this page',
      selectRow: 'Select row {id}',
    },
  },
  conflict: {
    title: 'Someone else changed this record',
    body: 'This is a shared dataset. Another user updated this record while you were editing it. Choose whether to apply your change anyway or keep theirs.',
    field: 'Field',
    expected: 'You expected',
    current: 'Currently stored',
    attempted: 'Your change',
    empty: '(empty)',
    keepTheirs: 'Keep their version',
    overwrite: 'Apply my change anyway',
    applying: 'Applying…',
  },
  notes: {
    eyebrow: 'Private',
    title: 'Notes',
    intro: 'Visible only to your account.',
    new: 'New note',
    titlePlaceholder: 'Title',
    bodyPlaceholder: 'Write…',
    untitled: 'Untitled',
    updatedAt: 'Updated {date}',
    empty: 'No notes yet.',
    deleteConfirm: 'Delete this note?',
  },
  exporter: {
    label: 'Export',
    nothing: 'Nothing to export',
    hint: 'Export what is shown',
    exported: '{n} row exported | {n} rows exported',
    busy: 'Exporting… {n} rows',
    csvHint: 'spreadsheet',
    jsonHint: 'full fidelity',
  },
  adminBatches: {
    eyebrow: 'Super admin',
    title: 'Data batches',
    intro:
      'The database holds one slice ("batch") of the 433,710-record source dump at a time. Swap to the next batch to work on records nobody has analysed yet, or reload a previous batch — the dump file is the archive, so every batch can be reproduced exactly. Past analyses and their results always survive a swap.',
    currentBatch: 'Current batch',
    explored: 'Batches explored',
    remaining: '{n} never loaded',
    recordsLoaded: 'EEG records loaded',
    batchGrid: 'All batches',
    gridHint:
      'Click a batch to select it, then load it with the buttons below. Numbers are batch positions in the dump; identical numbers always contain identical records.',
    legend: {
      current: 'Currently loaded',
      explored: 'Explored before',
      unexplored: 'Never loaded',
    },
    batchCell: 'Batch {n}',
    noSource:
      'No batch source is available. Configure the S4_* variables (production) or place the 72 GiB dump at {path} (see DUMP_PATH).',
    sourceS4: 'Batch source: MEGA S4 bucket “{bucket}” — a swap downloads one ~2 GB batch file.',
    sourceDump: 'Batch source: local dump file at {path}.',
    loadNext: 'Load next batch ({n})',
    loadSelected: 'Load batch {n}',
    revisit: 'already explored — loads exactly the records it had before',
    confirm: 'Click again to confirm',
    confirmHint:
      'This wipes the {n} currently loaded EEG records and loads the new batch in their place. Everyone else is locked out until it finishes. Results of past analyses are kept.',
    allExplored:
      'Every batch has been loaded at least once. Select any batch above to load it again.',
    swapWarning:
      'Swapping replaces the loaded raw EEG with a different batch. Past analyses keep their results — only the raw signal availability changes. New analyses are blocked and other users are locked out while the swap runs.',
    activeTitle: 'Swap in progress',
    startedBy: 'Started by {who} · {when}',
    status: {
      idle: 'Idle',
      draining: 'Waiting for running analyses to finish',
      downloading: 'Downloading batch {n} from the object store',
      wiping: 'Wiping the current batch (freeing disk space)',
      loading: 'Loading batch {n} into the database',
      failed: 'Swap failed',
    },
    runningJobs: '{n} analysis is still running | {n} analyses are still running',
    pendingCancelled:
      'New analyses are blocked; queued ones were cancelled. The swap starts as soon as nothing is running.',
    cancelJobs: 'Cancel the running analyses',
    cancelJobsConfirm: 'Really cancel them?',
    progressRows: '{scanned} of {total} dump records scanned · {kept} kept for this batch',
    progressDownload: '{mb} MB downloaded',
    progressCopy: '{kept} records copied into the database',
    usersLocked:
      'All other users see a lock screen until this finishes; the app then reloads itself everywhere.',
    failedTitle: 'The last swap failed',
    retryHint:
      'The load is safe to retry: starting a swap again wipes cleanly and reloads. If this keeps happening, check the backend logs.',
    strataTitle: 'Batch coverage by montage',
    strataIntro:
      'Rare montages have fewer records, so they are spread across fewer batches. Beyond their last batch they simply no longer appear — a record is never loaded twice.',
    strataRow: '{channels}-electrode records — present in batches 1 to {n}',
    strataRowUnknown: 'unknown-montage records — present in batches 1 to {n}',
  },
  swapLock: {
    title: 'Data swap in progress',
    body: 'The administrator is replacing the loaded EEG batch with a different slice of the dataset. Your work is safe — past runs, results and notes are untouched.',
    status: {
      idle: 'Finishing…',
      draining: 'Waiting for running analyses to finish',
      downloading: 'Fetching the new records',
      wiping: 'Clearing the current records',
      loading: 'Loading the new records',
      failed: 'Finishing…',
    },
    hint: 'This page will reload by itself when the new data is ready.',
  },
  embedding: {
    heatmapAria: 'Embedding heatmap, {n} dimensions',
    barsAria: 'Embedding dimension profile, {n} signed values',
    dimTooltip: 'dim {dim} · {value}',
    dimsRange: 'dims 0–{max}',
    aboveZero: 'above zero',
    belowZero: 'below zero',
    scatterCaption:
      'Each point is one recording’s mean-pooled embedding, projected onto the run’s top two principal components — PC1 {pc1}% · PC2 {pc2}% of variance. Points that sit together embed similarly.',
  },
};
